// SiteLock — whole-site gate. Passkey lives only on the server (SITE_LOCK_PASSKEY).
// Unlock state is held in memory only, so every refresh / new tab / direct visit re-asks.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { trackVisitorOnce } from '../lib/visitorTrack';

const API = process.env.REACT_APP_BACKEND_URL;

axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 423 && window.__lxtUnlocked && !window.__lxtLockReloading) {
      window.__lxtLockReloading = true;
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

export default function SiteLock({ children }) {
  const [state, setState] = useState('checking'); // checking | locked | open
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    localStorage.removeItem('lxt_site_unlocked');
    axios
      .post(`${API}/api/site-lock/lock`, {}, { withCredentials: true })
      .then(() => axios.get(`${API}/api/site-lock/status`, { withCredentials: true }))
      .then((r) => setState(r.data.enabled ? 'locked' : 'open'))
      .catch(() => setState('locked'));
  }, []);

  useEffect(() => {
    if (state === 'open') { window.__lxtUnlocked = true; trackVisitorOnce(); }
  }, [state]);

  if (state === 'open') return children;
  if (state === 'checking') return <div className="min-h-screen bg-white" data-testid="site-lock-checking" />;

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/site-lock/verify`, { passkey: value }, { withCredentials: true });
      setValue('');
      setState('open');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to verify. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-10 sm:px-6 bg-gradient-to-br from-teal-50 via-white to-blue-50"
      data-testid="site-lock-screen"
    >
      <div className="w-full max-w-sm bg-white/90 backdrop-blur rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 text-center">
        <div className="text-2xl sm:text-3xl font-bold text-teal-600 mb-4" data-testid="site-lock-logo">LearnixTa</div>

        <div className="mx-auto w-11 h-11 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center mb-6">
          <Lock size={18} className="text-teal-600" />
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="site-lock-error">{error}</div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={show ? 'text' : 'password'}
              value={value}
              onChange={(e) => { setValue(e.target.value); setError(''); }}
              placeholder="Passkey"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              className="w-full h-12 rounded-lg border border-gray-200 pl-10 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="site-lock-input"
            />
            <button type="button" onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid="site-lock-toggle" aria-label="Show or hide passkey">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <button type="submit" disabled={busy}
            className="w-full h-12 rounded-lg bg-teal-600 hover:bg-teal-700 active:scale-[0.99] disabled:opacity-60 text-white text-sm font-semibold transition-all"
            data-testid="site-lock-submit">
            {busy ? 'Checking…' : 'Enter Website'}
          </button>
        </form>
      </div>
    </div>
  );
}
